// File: src/components/ErrorBoundary.js

import React from 'react';
import { isChunkLoadFailure, recoverChunkLoadFailure } from '../utils/lazyWithReload';

const errorStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '40vh',
    padding: '32px 24px',
  },
  card: {
    maxWidth: 520,
    width: '100%',
    textAlign: 'center',
    background: 'rgba(18,18,24,0.92)',
    border: '1px solid rgba(224,120,48,0.3)',
    borderRadius: 16,
    padding: '40px 32px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  heading: {
    color: 'rgba(244,247,255,0.96)',
    fontSize: '1.5rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
    margin: '0 0 12px 0',
    fontFamily: '"JetBrains Mono", monospace',
  },
  body: {
    color: 'rgba(203,213,225,0.85)',
    fontSize: '1rem',
    lineHeight: 1.6,
    margin: '0 0 24px 0',
  },
  label: {
    display: 'inline-block',
    fontSize: '0.75rem',
    color: 'rgba(148,163,184,0.7)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 16,
    fontFamily: '"JetBrains Mono", monospace',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 28px',
    fontSize: '0.9rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#fff',
    background: 'linear-gradient(135deg, rgba(224,120,48,0.92), rgba(224,120,48,0.68))',
    border: '1px solid rgba(224,120,48,0.5)',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 200ms ease',
    fontFamily: '"JetBrains Mono", monospace',
  },
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const label = this.props.componentName || 'Unknown';
    console.error(`[ErrorBoundary:${label}] Crash:`, error, errorInfo);
  }

  handleRetry() {
    if (isChunkLoadFailure(this.state.error)) {
      const recovered = recoverChunkLoadFailure(this.props.componentName || 'error-boundary', { reset: true });
      if (recovered) return;
    }
    // Hard refresh to fetch the current build manifest and fresh scripts
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('__tgv_reload', String(Date.now()));
        window.location.replace(url.toString());
        return;
      } catch {
        window.location.reload();
        return;
      }
    }
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      const label = this.props.componentName || 'this section';
      const chunkFailure = isChunkLoadFailure(this.state.error);
      return (
        <div style={errorStyles.container}>
          <div style={errorStyles.card}>
            <span style={errorStyles.label}>SYSTEM // RECOVERY</span>
            <h2 style={errorStyles.heading}>Something went wrong</h2>
            <p style={errorStyles.body}>
              An unexpected error occurred in <strong>{label}</strong>.
              Your data is safe — try reloading this section or refreshing the page.
            </p>
            {this.state.error?.message && (
              <p style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.78rem',
                color: 'rgba(255, 140, 140, 0.95)',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 100, 100, 0.2)',
                padding: '8px 12px',
                borderRadius: '6px',
                margin: '0 0 20px 0',
                wordBreak: 'break-word',
                textAlign: 'left',
              }}>
                {this.state.error.message}
              </p>
            )}
            <button
              onClick={this.handleRetry}
              style={errorStyles.button}
              onMouseOver={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(224,120,48,1), rgba(224,120,48,0.82)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(224,120,48,0.92), rgba(224,120,48,0.68)'; }}
            >
              {chunkFailure ? 'Reload Page' : 'Try Again / Reload'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
